/* buildCurrentState.ts */

/* Interfaces */
import {
  AgentAnalysis,
  AgentCurrentState,
} from "src/shared/interfaces/api/AgentSpec";
import { RootState } from "src/redux/store";

/**
 * Years are strings in the filter state but numbers on the wire.
 * Anything unparseable is omitted rather than sent as NaN.
 */
const toYear = (value?: string): number | undefined => {
  if (!value) return undefined;
  const year = Number(value);
  return Number.isFinite(year) ? year : undefined;
};

const toStringList = (
  items?: string[] | unknown[]
): string[] => (items ?? []).map((item) => String(item));

/**
 * Snapshot the controls for the agent.
 *
 * The option lists matter as much as the values: they are the only
 * values the agent may choose from, which is what keeps a generated
 * spec valid for whatever data this deployment has.
 */
export const buildAgentCurrentState = (
  state: RootState,
  analysis: AgentAnalysis | null
): AgentCurrentState => {
  const {
    mainCondition,
    features,
    cohorts,
    customFeature,
    timePeriod,
    careSites,
  } = state.insightsFilter;

  const { startPoint } = state.survivalAnalysisOptions;
  const startPoints = startPoint.items;

  return {
    analysis,
    main_condition: mainCondition.value ?? undefined,
    cohort: cohorts?.value,
    feature: features?.value,
    custom_feature: customFeature?.value,
    start_point: startPoint.value,
    max_depth: state.sankeyDiagramOptions.maxDepth,
    first_year: toYear(timePeriod?.value?.firstYear),
    last_year: toYear(timePeriod?.value?.lastYear),
    care_sites: (careSites?.value as string[]) ?? [],
    available_main_conditions: toStringList(
      mainCondition.items
    ),
    available_features: toStringList(features?.items),
    available_cohorts: toStringList(cohorts?.items),
    available_custom_features: toStringList(
      customFeature?.items
    ),
    available_start_points: toStringList(startPoints),
    /**
     * The care sites the user may actually select, so the agent cannot
     * propose one the backend would reject with a 403.
     *
     * The fallback is not a wider list: `careSites.items` is only ever
     * populated from `allowedCareSites` (see `initCareSites`), so it is
     * a subset of it by construction.
     */
    available_care_sites:
      state.auth.allowedCareSites ??
      toStringList(careSites?.items),
    // initialValue holds the range the backend reported for this
    // main condition, which is the authoritative available range.
    available_first_year: toYear(
      timePeriod?.initialValue?.firstYear
    ),
    available_last_year: toYear(
      timePeriod?.initialValue?.lastYear
    ),
  };
};
